import { produce } from 'immer'
import { isEmpty, isNil } from 'lodash'

import { END_CHAT } from './realtimeChats.redux'

// Action Types
export const ADD_TRANSCRIPT_UTTERANCE = 'liveTranscripts/addUtterance'
export const CLEAR_AGENT_TRANSCRIPT = 'liveTranscripts/clearAgentTranscript'
// The call is over but its words stay on screen, marked as finished. Only the
// NEXT call throws them away — a supervisor who looks up a second after hangup
// should still see what was said, not an empty panel.
export const END_AGENT_TRANSCRIPT = 'liveTranscripts/endAgentTranscript'
export const OPEN_TRANSCRIPT = 'liveTranscripts/openTranscript'
export const CLOSE_TRANSCRIPT = 'liveTranscripts/closeTranscript'
export const TOGGLE_TRANSCRIPT = 'liveTranscripts/toggleTranscript'
// Tells the Command Center socket which agent's utterances to relay. The RTC
// socket needs no equivalent — it already pushes every supervised agent.
export const WATCH_TRANSCRIPT = 'liveTranscripts/watchTranscript'
// Echoed back by ccSocketMiddleware once the watch actually reached the socket
// worker. The RTC socket steps aside for a watched agent, so this must reflect
// a relay that is really running, not one we merely asked for.
export const SET_WATCHED_AGENT = 'liveTranscripts/setWatchedAgent'
// The server answers every watch, accepted or refused, so a manager whose
// request was denied gets told instead of staring at a blank panel.
export const SET_WATCH_STATUS = 'liveTranscripts/setWatchStatus'

// A busy call produces an utterance every couple of seconds and a supervisor
// can leave the panel open for hours. Keep the tail the panel can actually
// scroll and drop the rest — there is no scrollback UI and no backfill.
export const MAX_UTTERANCES_PER_AGENT = 300

// One speaker can hold the floor indefinitely (hold music, an IVR, one-way
// audio), and merging every chunk into one block would grow a single string
// without bound and re-render it on each utterance. Break the run instead.
export const MAX_MERGED_BLOCK_CHARS = 1000

export const closeTranscript = () => ({ type: CLOSE_TRANSCRIPT })
export const toggleTranscript = () => ({ type: TOGGLE_TRANSCRIPT })
export const watchTranscript = (agentUsername) => ({
  type: WATCH_TRANSCRIPT,
  agentUsername,
  watching: true,
})
export const unwatchTranscript = (agentUsername) => ({
  type: WATCH_TRANSCRIPT,
  agentUsername,
  watching: false,
})
export const endAgentTranscript = (agentUsername) => ({
  type: END_AGENT_TRANSCRIPT,
  agentUsername,
})
export const addTranscriptUtterance = (agentUsername, utterance) => ({
  type: ADD_TRANSCRIPT_UTTERANCE,
  agentUsername,
  utterance,
})

export const selectAgentTranscript = (state, agentUsername) =>
  state.liveTranscripts.byAgent[agentUsername] || []

export const selectAgentCallEnded = (state, agentUsername) =>
  state.liveTranscripts.endedAgents.includes(agentUsername)

export const initialState = {
  // { [agentUsername]: [{ id, text, side, time, endTime, seq, droppedBefore }] }
  // `side` is 0 for the agent and 1 for the customer, matching the desktop's
  // 'me' / 'them'. `seq` is the relay's number for the LAST chunk merged into
  // the block, and `droppedBefore` marks a block the relay skipped ahead to.
  byAgent: {},
  // Monotonic, so React keys stay unique when two utterances land in the same
  // millisecond.
  nextUtteranceId: 1,
  // Agents the Command Center socket is relaying. The RTC socket reads this to
  // stay out of the way for them. A list, not one agent: the socket is shared
  // across tabs, so more than one can be live at a time.
  watchedAgents: [],
  // { [agentUsername]: { status: 'watching' | 'denied', reason } }
  watchStatus: {},
  // Agents whose call has hung up. Their utterances are still in `byAgent` and
  // still rendered; the flag is what turns the panel into a closed record.
  endedAgents: [],
  open: false,
}

export default function liveTranscripts(state = initialState, action) {
  switch (action.type) {
    case ADD_TRANSCRIPT_UTTERANCE:
      // A frame with no agent or no words is noise: without this it would
      // create a `byAgent[undefined]` bucket, or pad the previous block with a
      // bare space.
      if (isEmpty(action.agentUsername) || isEmpty(action.utterance?.text)) {
        return state
      }

      return produce(state, (newState) => {
        // First words of a new call. The finished call's transcript is still on
        // screen, so drop it now rather than let the two run together. CALL_START
        // normally gets here first; this covers the case where it doesn't.
        if (newState.endedAgents.includes(action.agentUsername)) {
          delete newState.byAgent[action.agentUsername]
          newState.endedAgents = newState.endedAgents.filter(
            (agentUsername) => agentUsername !== action.agentUsername
          )
        }

        const utterances = newState.byAgent[action.agentUsername] || []
        const latest = utterances[utterances.length - 1]
        // The relay stamps a monotonic seq per agent. A jump means the server
        // failed to write a line (it swallows that error), so the next one must
        // NOT be merged onto the previous speaker — that would read as a
        // sentence nobody said.
        //
        // `latest.seq` is the seq of the LAST chunk merged into the block, not
        // the first, so a long run of speech stays contiguous.
        const expectedSeq = isNil(latest?.seq) ? null : latest.seq + 1
        const droppedBefore =
          !isNil(action.utterance.seq) && !isNil(expectedSeq) && action.utterance.seq > expectedSeq

        // Speech arrives in chunks. Consecutive chunks from the same speaker are
        // one utterance — merge them and stretch the block's end time.
        if (
          latest &&
          !droppedBefore &&
          latest.side === action.utterance.side &&
          latest.text.length < MAX_MERGED_BLOCK_CHARS
        ) {
          latest.text = `${latest.text} ${action.utterance.text}`
          latest.endTime = action.utterance.time
          // Only a sequenced line may move the block's seq. The RTC socket
          // carries no seq, so assigning it unconditionally wiped the block's
          // number and switched gap detection off for the rest of the call.
          if (!isNil(action.utterance.seq)) {
            latest.seq = action.utterance.seq
          }
        } else {
          utterances.push({
            ...action.utterance,
            id: newState.nextUtteranceId,
            endTime: action.utterance.time,
            // Rendered as a break so the supervisor can see something is missing.
            droppedBefore,
          })
          newState.nextUtteranceId += 1
        }

        newState.byAgent[action.agentUsername] = utterances.slice(-MAX_UTTERANCES_PER_AGENT)
      })
    case CLEAR_AGENT_TRANSCRIPT:
    case END_CHAT:
      return produce(state, (newState) => {
        const agentUsername = action.agentUsername || action.username
        delete newState.byAgent[agentUsername]
        newState.endedAgents = newState.endedAgents.filter(
          (endedUsername) => endedUsername !== agentUsername
        )
      })
    case END_AGENT_TRANSCRIPT:
      return produce(state, (newState) => {
        if (!newState.endedAgents.includes(action.agentUsername)) {
          newState.endedAgents.push(action.agentUsername)
        }
      })
    case SET_WATCHED_AGENT:
      return produce(state, (newState) => {
        if (action.watching) {
          if (!newState.watchedAgents.includes(action.agentUsername)) {
            newState.watchedAgents.push(action.agentUsername)
          }
        } else {
          newState.watchedAgents = newState.watchedAgents.filter(
            (agentUsername) => agentUsername !== action.agentUsername
          )
        }
      })
    case SET_WATCH_STATUS:
      return produce(state, (newState) => {
        newState.watchStatus[action.agentUsername] = {
          status: action.status,
          reason: action.reason || null,
        }
      })
    case OPEN_TRANSCRIPT:
      return produce(state, (newState) => {
        newState.open = true
      })
    case CLOSE_TRANSCRIPT:
      return produce(state, (newState) => {
        newState.open = false
      })
    case TOGGLE_TRANSCRIPT:
      return produce(state, (newState) => {
        newState.open = !newState.open
      })
    default:
      return state
  }
}
